import { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import parseError from "app/lib/parseError";
import { getProfiles } from "app/api/profile";
import {
  useActiveProfile,
  useSetActiveProfile,
} from "app/providers/ActiveProfileProvider";
import { PROFILES_QUERY } from "app/constants/commonQueries";
import { parseToken } from "app/lib/encryption";
import { useProfile } from "app/providers/ProfileProvider";
import { ActiveProfile } from "app/models/profile";
import { ACTIVE_PROFILE_ID_QUERY_PARAM } from "app/constants/variables";

/**
 * Custom hook to get user profiles.
 *
 * @param enabled - A boolean to enable or disable the fetching of profiles.
 */

const useGetProfiles = (enabled = true, token?: string) => {
  const activeProfile = useActiveProfile();
  const setActiveProfile = useSetActiveProfile();
  const profile = useProfile();
  const [searchParams] = useSearchParams();
  const activeProfileId = searchParams.get(ACTIVE_PROFILE_ID_QUERY_PARAM);

  const { data, status, error, refetch, isFetching, isLoading } = useQuery(
    [PROFILES_QUERY],
    () => getProfiles(token),
    {
      enabled,
      // 5 minutes
      staleTime: 1000 * 60 * 5,
    }
  );

  useEffect(() => {
    if (!data) return;

    const profiles = data.data.data as ActiveProfile[];

    if (activeProfile || activeProfileId) {
      const token = parseToken();

      let defaultProfile = profiles.find(
        (p) => p.id === (activeProfileId ?? activeProfile?.id)
      );

      if (!defaultProfile) {
        defaultProfile =
          profiles.find((p) => p.profile_type === "personal") || profiles[0];
      }

      if ((token?.uid as string) === defaultProfile.user_id) {
        setActiveProfile(defaultProfile);
        return;
      }
    }

    const defaultSelectedProfile = profile?.business_default
      ? "business"
      : activeProfile?.profile_type || "personal";

    // Profile selected by default
    const defaultProfile = profiles.find(
      (p) => p.profile_type === defaultSelectedProfile
    );

    setActiveProfile(defaultProfile ?? profiles[0]);
  }, [data, setActiveProfile, profile, activeProfile, activeProfileId]);

  return {
    profiles: data?.data.data,
    status,
    error: error ? (parseError(error as AxiosError<{}>) as string) : undefined,
    getProfiles: refetch,
    isFetching,
    isLoading,
  };
};

export default useGetProfiles;
